// XP System for TopBlast Platform
// Based on TopBlast Development Plan specifications

// TopBlast Ranking System - Exact as specified
export const VERIFICATION_TIERS = {
  PAPERHANDS: {
    id: 'paperhands',
    name: 'Paper Hands',
    rank: 0,
    xpRequired: 0,
    checkmark: null,
    color: '#9CA3AF',
    postLimit: 5,
    canLoseXP: false,
    description: 'Rank 0: Safe zone - no XP loss, learn to predict'
  },
  FARTMARK: {
    id: 'fartmark',
    name: 'Fartmark',
    rank: 1,
    xpRequired: 501,
    checkmark: '✓',
    color: '#8B4513',
    postLimit: 10,
    canLoseXP: true,
    description: 'Rank 1: Brown checkmark - basic prediction skills'
  },
  GOLD: {
    id: 'gold',
    name: 'Gold',
    rank: 2,
    xpRequired: 10000,
    checkmark: '✓',
    color: '#FFD700',
    postLimit: 25,
    canLoseXP: true,
    description: 'Rank 2: Gold checkmark - skilled prediction maker'
  },
  DIAMOND: {
    id: 'diamond',
    name: 'Diamond',
    rank: 3,
    xpRequired: 50000,
    checkmark: '✓',
    color: '#B9F2FF',
    postLimit: 100,
    canLoseXP: true,
    description: 'Rank 3: Diamond checkmark - elite prediction master'
  },
  OBSIDIAN: {
    id: 'obsidian',
    name: 'Obsidian',
    rank: 4,
    xpRequired: 200000,
    checkmark: '✓',
    color: '#1F2937',
    postLimit: -1,
    canLoseXP: true,
    description: 'Rank 4: Obsidian checkmark - legendary prediction god'
  }
};

// Get verification tier based on XP
export const getVerificationTier = (xp) => {
  const tiers = Object.values(VERIFICATION_TIERS).reverse(); // Start from highest
  return tiers.find(tier => xp >= tier.xpRequired) || VERIFICATION_TIERS.PAPERHANDS;
};

// Calculate XP for a pump detection post
export const calculateXP = ({
  priceMultiplier = 1,
  volumeWeight = 1,
  accuracyFactor = 1,
  detectionTime = Date.now(),
  announcementTime = null,
  isVerified = false
}) => {
  // Base XP calculation from TopBlast plan
  // XP_base = log(price_multiplier) × volume_weight × accuracy_factor
  const XP_base = Math.log(priceMultiplier) * volumeWeight * accuracyFactor;
  
  // Early detection bonus
  // Early_bonus = max(0, (announcement_time - detection_time) / 3600) × 2.5
  let earlyBonus = 0;
  if (announcementTime && detectionTime < announcementTime) {
    const hoursDifference = (announcementTime - detectionTime) / (1000 * 60 * 60);
    earlyBonus = Math.max(0, hoursDifference) * 2.5;
  }
  
  // Final XP calculation
  // XP_final = XP_base × (1 + Early_bonus)
  const XP_final = XP_base * (1 + earlyBonus);
  
  // Round to reasonable number
  return Math.max(1, Math.round(XP_final));
};

// Check if user can post based on verification tier and daily limit
export const canUserPost = (user, dailyPostCount = 0) => {
  const tier = getVerificationTier(user.xp || 0);
  
  // Unlimited posts for Diamond and Obsidian (Rank 3 & 4)
  if (tier.postLimit === -1) {
    return { canPost: true, remaining: -1 };
  }
  
  const remaining = tier.postLimit - dailyPostCount;
  return {
    canPost: remaining > 0,
    remaining: Math.max(0, remaining)
  };
};

// Check if user can lose XP based on rank
export const canUserLoseXP = (user) => {
  const tier = getVerificationTier(user.xp || 0);
  return tier.canLoseXP;
};

// Format XP display
export const formatXP = (xp) => {
  if (xp >= 1000000) {
    return `${(xp / 1000000).toFixed(1)}M XP`;
  } else if (xp >= 1000) {
    return `${(xp / 1000).toFixed(1)}k XP`;
  }
  return `${xp} XP`;
};

// Get next tier info
export const getNextTier = (currentXP) => {
  const currentTier = getVerificationTier(currentXP);
  const allTiers = Object.values(VERIFICATION_TIERS);
  const currentIndex = allTiers.findIndex(tier => tier.id === currentTier.id);
  
  if (currentIndex === -1 || currentIndex === allTiers.length - 1) {
    return null; // Already at highest tier
  }
  
  const nextTier = allTiers[currentIndex + 1];
  const xpNeeded = nextTier.xpRequired - currentXP;
  
  return {
    tier: nextTier,
    xpNeeded: Math.max(0, xpNeeded),
    progress: currentXP / nextTier.xpRequired
  };
};

// Admin function to manually set verification
export const setManualVerification = (userId, tierLevel, reason = 'Manual verification') => {
  // This would integrate with your admin system
  // For now, return the structure that should be saved to database
  return {
    userId,
    tierLevel,
    reason,
    setAt: Date.now(),
    setBy: 'admin' // Would be actual admin user ID
  };
};

// Validate XP transaction
export const validateXPTransaction = (transaction) => {
  const required = ['userId', 'xpAmount', 'reason', 'sourcePost'];
  return required.every(field => transaction[field] !== undefined);
};

const xpUtils = {
  VERIFICATION_TIERS,
  getVerificationTier,
  calculateXP,
  canUserPost,
  canUserLoseXP,
  formatXP,
  getNextTier,
  setManualVerification,
  validateXPTransaction
};

export default xpUtils;